import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, StaggerWords } from "../components/Reveal";
import Milestones from "../components/Milestones";
import { fetchServices, fetchInsights, fetchTeam } from "../lib/api";
import { useFirm } from "../context/FirmContext";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const Hero = () => {
  const firm = useFirm();
  return (
    <section data-testid="hero-section" className="relative min-h-[88svh] flex flex-col">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full pt-6 lg:pt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 flex-1">
        <div className="lg:col-span-7 flex flex-col justify-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-8">
              — Chartered Accountancy &amp; Advisory · Chandigarh
            </p>
          </Reveal>
          <h1 className="font-serif text-[14vw] sm:text-[10vw] lg:text-[7.2vw] font-light tracking-[-0.02em] leading-[0.92]">
            <StaggerWords text="Counsel for the" />
            <br />
            <span className="italic font-light"><StaggerWords text="considered." delay={0.3} /></span>
            <br />
            <StaggerWords text="Clarity for the" delay={0.5} />
            <br />
            <span className="italic font-light"><StaggerWords text="complex." delay={0.8} /></span>
          </h1>
          <Reveal delay={1.0}>
            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <Link to="/services" data-testid="hero-cta-services" className="btn-editorial">
                The practice <ArrowUpRight size={14} />
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact" className="editorial-link text-[11px] tracking-[0.28em] uppercase text-neutral-700">
                Speak with a partner →
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5 flex flex-col justify-end">
          <Reveal delay={0.4}>
            <div className="img-frame border border-[var(--rule)] aspect-[4/5] mb-8">
              <img
                src="https://images.unsplash.com/photo-1619504623861-0f6117da1fd4"
                alt="Architectural facade"
                className="w-full h-full object-cover grayscale"
              />
            </div>
          </Reveal>
          <Reveal delay={0.6}>
            <p className="font-serif text-xl lg:text-2xl text-neutral-700 leading-snug max-w-md">
              {firm?.description || "A boutique chartered accountancy practice. Quietly rigorous. Unmistakably partner-led."}
            </p>
          </Reveal>
        </div>
      </div>

      {/* Bottom rule */}
      <div className="border-t border-[var(--rule)] mt-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 grid grid-cols-2 lg:grid-cols-4 gap-6 text-[11px] tracking-[0.28em] uppercase text-neutral-500">
          <span>Established 2017</span>
          <span>Chandigarh · India</span>
          <span className="hidden lg:block">3 Partners · Lean by design</span>
          <span className="hidden lg:block text-right">Edition · {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</span>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => (
  <div className="bg-neutral-950 text-neutral-50 py-6 overflow-hidden border-y border-neutral-900">
    <div className="flex gap-16 whitespace-nowrap marquee-track">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex gap-16 items-center shrink-0">
          <span className="font-serif text-3xl italic font-light">Litigation</span>
          <span className="text-neutral-700">/</span>
          <span className="font-serif text-3xl">Cross-Border Consulting</span>
          <span className="text-neutral-700">/</span>
          <span className="font-serif text-3xl italic font-light">Assurance</span>
          <span className="text-neutral-700">/</span>
          <span className="font-serif text-3xl">Finance Advisory</span>
          <span className="text-neutral-700">/</span>
          <span className="font-serif text-3xl italic font-light">Loan Syndication</span>
          <span className="text-neutral-700">/</span>
        </div>
      ))}
    </div>
  </div>
);

const ServicesRibbon = () => {
  const [services, setServices] = useState([]);
  useEffect(() => { fetchServices().then(setServices).catch(() => {}); }, []);

  return (
    <section data-testid="services-section" className="py-24 lg:py-32 border-b border-[var(--rule)]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <Reveal className="lg:col-span-4">
            <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-4">— The practice</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]">
              Four disciplines.
              <br />
              <span className="italic">One standard.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="lg:col-span-7 lg:col-start-6 flex items-end">
            <p className="font-serif text-xl lg:text-2xl text-neutral-700 leading-snug">
              We do a few things, with care. Each engagement is led by a partner whose name appears
              on the opinion — and stays on it through the audit, the appeal, or the assessment.
            </p>
          </Reveal>
        </div>

        <div className="border-t border-neutral-900">
          {services.map((s, idx) => (
            <Reveal key={s.id} delay={idx * 0.1}>
              <Link
                to={`/services#${s.slug}`}
                data-testid={`services-row-${s.slug}`}
                className="group block border-b border-neutral-900 py-8 lg:py-12 transition-colors hover:bg-neutral-950 hover:text-neutral-50"
              >
                <div className="grid grid-cols-12 gap-6 items-baseline">
                  <span className="col-span-2 lg:col-span-1 marker text-3xl lg:text-5xl text-neutral-400 group-hover:text-neutral-500">
                    {s.number}
                  </span>
                  <h3 className="col-span-10 lg:col-span-4 font-serif text-4xl lg:text-6xl font-light tracking-tight leading-none">
                    {s.title}
                  </h3>
                  <p className="col-span-12 lg:col-span-6 text-base lg:text-lg leading-snug text-neutral-600 group-hover:text-neutral-300">
                    {s.summary}
                  </p>
                  <ArrowUpRight
                    size={28}
                    className="col-span-12 lg:col-span-1 justify-self-end transition-transform duration-500 group-hover:rotate-45"
                  />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const About = () => {
  const firm = useFirm();
  return (
    <section data-testid="about-snippet" className="py-24 lg:py-32 border-b border-[var(--rule)]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="img-frame border border-[var(--rule)] aspect-[3/4]">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_attry-assurance/artifacts/58dlp9mk_simone-hutsch-iDSfeuoxM0o-unsplash.webp"
              alt="Attry & Associates — practice"
              className="w-full h-full object-cover grayscale"
            />
          </div>
        </Reveal>
        <Reveal delay={0.2} className="lg:col-span-6 lg:col-start-7 flex flex-col justify-center">
          <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-6">— On the firm</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] mb-8">
            A practice built on
            <br />
            <span className="italic">unhurried judgment.</span>
          </h2>
          <div className="columns-1 sm:columns-2 gap-8 text-base leading-relaxed text-neutral-700 space-y-4">
            <p>
              Founded in {firm?.established || "2017"} by {firm?.founder || "CA. Anmol Attry"},
              the firm advises a deliberately small roster of clients — closely-held groups,
              family offices, and companies with cross-border footprints.
            </p>
            <p>
              We take the matters that interest us, and we take them seriously. There is no
              second tier, no associate-led delivery, no hand-off after the pitch.
            </p>
          </div>
          <div className="mt-10">
            <Link to="/team" data-testid="about-cta-team" className="btn-editorial">
              The partners <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Stats = () => (
  <section className="py-20 border-b border-[var(--rule)]" data-testid="stats-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 divide-x divide-[var(--rule)]">
      {[
        ["8+", "Years in practice"],
        ["3", "Partners · Hands-on"],
        ["240+", "Engagements concluded"],
        ["12", "Jurisdictions advised"],
      ].map(([n, l], i) => (
        <Reveal key={i} delay={i * 0.08} className="px-4 lg:px-8 first:pl-0 last:pr-0">
          <div className="font-serif text-5xl lg:text-7xl font-light tracking-tighter leading-none">{n}</div>
          <div className="text-[11px] tracking-[0.28em] uppercase text-neutral-500 mt-3">{l}</div>
        </Reveal>
      ))}
    </div>
  </section>
);

const InsightsPreview = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => { fetchInsights().then(setPosts).catch(() => {}); }, []);
  const featured = posts.find((p) => p.featured) || posts[0];
  const rest = posts.filter((p) => p?.id !== featured?.id).slice(0, 3);

  return (
    <section data-testid="insights-preview" className="py-24 lg:py-32 border-b border-[var(--rule)]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-6">
          <Reveal>
            <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-4">— Published every week</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
              The <span className="italic">Column.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/insights" data-testid="insights-view-all" className="editorial-link text-[11px] tracking-[0.28em] uppercase">
              View all writing →
            </Link>
          </Reveal>
        </div>

        {featured && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 border-t border-neutral-900 pt-10">
            {/* Left — Weekly cadence pillar */}
            <Reveal className="lg:col-span-3">
              <div className="border-r-0 lg:border-r border-[var(--rule)] lg:pr-8 h-full flex flex-col justify-between">
                <div>
                  <p className="text-[10px] tracking-[0.32em] uppercase text-neutral-500">— Weekly cadence</p>
                  <p className="font-serif text-3xl lg:text-4xl font-light tracking-tight leading-[1.1] mt-6">
                    A new column,
                    <br />
                    <span className="italic">every Friday.</span>
                  </p>
                  <p className="text-sm text-neutral-600 mt-6 leading-relaxed">
                    Notes, opinions and unhurried analysis from the partners — written in-house,
                    published every week. No bulletins, no SEO copy. Only writing we'd want to
                    read ourselves.
                  </p>
                </div>
                <div className="mt-10 pt-6 border-t border-[var(--rule)] flex items-center justify-between text-[10px] tracking-[0.28em] uppercase text-neutral-500">
                  <span>Vol. {Math.max(1, new Date().getFullYear() - 2017)}</span>
                  <span>52 / year</span>
                </div>
              </div>
            </Reveal>

            {/* Middle — Featured */}
            <Reveal delay={0.1} className="lg:col-span-6">
              <Link to={`/insights/${featured.slug}`} data-testid="featured-insight" className="block group">
                <div className="img-frame border border-[var(--rule)] aspect-[16/10] mb-6">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover grayscale" />
                </div>
                <div className="flex gap-4 text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-3">
                  <span>This week</span><span>·</span><span>{featured.category}</span><span>·</span><span>{formatDate(featured.date)}</span>
                </div>
                <h3 className="font-serif text-3xl lg:text-5xl font-light tracking-tight leading-[1.05] group-hover:italic transition-all">
                  {featured.title}
                </h3>
                <p className="mt-4 text-base text-neutral-600 max-w-2xl leading-relaxed">{featured.excerpt}</p>
              </Link>
            </Reveal>

            {/* Right — Recent list */}
            <div className="lg:col-span-3 flex flex-col">
              <p className="text-[10px] tracking-[0.32em] uppercase text-neutral-500 mb-4">— In recent weeks</p>
              {rest.map((p, i) => (
                <Reveal key={p.id} delay={0.2 + i * 0.1}>
                  <Link
                    to={`/insights/${p.slug}`}
                    data-testid={`insight-card-${p.slug}`}
                    className="block border-t border-[var(--rule)] py-5 group"
                  >
                    <div className="flex gap-3 text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-2">
                      <span>{p.category}</span><span>·</span><span>{formatDate(p.date)}</span>
                    </div>
                    <h4 className="font-serif text-xl font-light tracking-tight leading-snug group-hover:italic transition-all">
                      {p.title}
                    </h4>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const TeamStrip = () => {
  const [team, setTeam] = useState([]);
  useEffect(() => { fetchTeam().then(setTeam).catch(() => {}); }, []);

  return (
    <section className="py-24 lg:py-32 border-b border-[var(--rule)]" data-testid="team-strip">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-4">— The partners</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] mb-14">
            Three names. <span className="italic">Three signatures.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-neutral-900">
          {team.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.1}>
              <div className="border-b border-neutral-900 md:border-b-0 md:border-r last:md:border-r-0 p-6 lg:p-10 h-full flex flex-col">
                <span className="marker text-3xl text-neutral-400 mb-6">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-4">{m.title}</p>
                <h3 className="font-serif text-3xl lg:text-4xl font-light leading-[1.05] tracking-tight">
                  {m.name.split(" ").slice(0, -1).join(" ")}{" "}
                  <span className="italic">{m.name.split(" ").slice(-1)}</span>
                </h3>
                <p className="text-sm text-neutral-600 mt-6 leading-relaxed">{m.specialization}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12">
          <Link to="/team" data-testid="team-cta" className="btn-editorial">
            Read partner bios <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const ContactStrip = () => {
  const firm = useFirm();
  return (
    <section data-testid="contact-strip" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-7">
          <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-6">— Begin a conversation</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
            We respond to <span className="italic">considered</span> enquiries.
          </h2>
        </Reveal>
        <Reveal delay={0.2} className="lg:col-span-5 lg:col-start-8 flex flex-col justify-end gap-6">
          <a
            href={`mailto:${firm?.email || "anmol@attryassociates.in"}`}
            data-testid="home-contact-email"
            className="font-serif text-2xl lg:text-3xl font-light editorial-link self-start break-all"
          >
            {firm?.email || "anmol@attryassociates.in"}
          </a>
          {firm?.phone && (
            <a
              href={`tel:${firm.phone.replace(/\s/g, "")}`}
              data-testid="home-contact-phone"
              className="font-serif text-2xl lg:text-3xl font-light editorial-link self-start"
            >
              {firm.phone}
            </a>
          )}
          <Link to="/contact" data-testid="home-contact-cta" className="btn-editorial self-start mt-2">
            Office details <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <ServicesRibbon />
    <Stats />
    <About />
    <Milestones />
    <InsightsPreview />
    <TeamStrip />
    <ContactStrip />
  </>
);

export default Home;
